/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService, IPage } from '../authentication/auth.service';
import { AdminPageService } from '@shared/services/api/admin/admin-page.service';
import { getPageAvatar } from '@shared/utils';

export interface ISwitchBusinessResult {
  success: boolean;
  businessId: number;
  businessName: string;
  pageCount: number;
  messages?: string[];
}

/**
 * Service quản lý chuyển đổi ngữ cảnh doanh nghiệp dành riêng cho Admin/SuperAdmin.
 * Cho phép Admin vào bất kỳ doanh nghiệp nào bằng cách truyền tham số ?businessId=xxx trên URL.
 * 
 * NGUYÊN TẮC AN TOÀN TUYỆT ĐỐI:
 * - Chỉ hoạt động khi người dùng là Admin hoặc SuperAdmin (authService.isAdmin() === true).
 * - Người dùng thông thường (staff, manager, member, page_admin, guest) TUYỆT ĐỐI KHÔNG BỊ ẢNH HƯỞNG.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminBusinessContextService {
  private authService = inject(AuthService);
  private adminPageService = inject(AdminPageService);

  /**
   * Kiểm tra xem user hiện tại có quyền impersonate sang doanh nghiệp khác hay không.
   * Chỉ cho phép khi user là Admin/SuperAdmin và ID doanh nghiệp hợp lệ.
   * 
   * @param businessId ID của doanh nghiệp cần kiểm tra
   */
  canImpersonate(businessId: unknown): boolean {
    // 1. Chỉ Admin hoặc SuperAdmin mới có quyền
    if (!this.authService.isAdmin()) {
      return false;
    }

    // 2. ID doanh nghiệp phải là số hợp lệ > 0
    const bId = Number(businessId);
    if (!Number.isFinite(bId) || bId <= 0) {
      return false;
    }

    // 3. Chỉ kích hoạt tính năng admin impersonate khi doanh nghiệp này Admin CHƯA login trong phiên này
    return !this.authService.isBusinessLoggedIn(bId);
  }

  /**
   * Kiểm tra xem Admin đã đang ở đúng ngữ cảnh của doanh nghiệp này và đã có pages chưa.
   * Dùng để tránh gọi lại API không cần thiết khi navigate cùng doanh nghiệp.
   * 
   * @param businessId ID của doanh nghiệp mục tiêu
   */
  isAlreadyInBusiness(businessId: number | string): boolean {
    const bId = Number(businessId);
    if (!Number.isFinite(bId) || bId <= 0) {
      return false;
    }

    // Kiểm tra đồng thời activeBusiness hiện tại và auth.pages đã có trang của doanh nghiệp này chưa
    const currentActiveId = this.authService.activeBusiness?.id;
    const hasPages = this.authService.hasPagesOfBusiness(bId);

    return Number(currentActiveId) === bId && hasPages;
  }

  /**
   * Chuyển đổi ngữ cảnh làm việc của Admin sang một doanh nghiệp cụ thể:
   * 1. Gọi API /admin/page/list để lấy danh sách toàn bộ các page thuộc doanh nghiệp đó.
   * 2. Lấy tên doanh nghiệp từ kết quả API.
   * 3. Thiết lập quyền Giám đốc doanh nghiệp (SYSTEM_ROLE_MANAGER) cho context này.
   * 4. Tự động chọn tất cả các page và lưu vào auth, activeBusiness, localStorage như khi Giám đốc login.
   * 5. Phát tín hiệu onAuthChanged$ để toàn bộ giao diện và bộ lọc page cập nhật tức thì.
   * 
   * @param businessId ID của doanh nghiệp mục tiêu
   * @returns Observable<boolean> - Trả về true nếu chuyển đổi thành công hoặc hoàn tất fallback
   */
  switchBusinessForAdmin(businessId: number | string): Observable<boolean> {
    const bId = Number(businessId);

    // 1. Guard an toàn: Nếu không phải Admin hoặc businessId không hợp lệ -> thoát ngay
    if (!this.canImpersonate(bId)) {
      return of(false);
    }

    // 2. Nếu đã ở đúng doanh nghiệp này và đã có danh sách page -> không cần gọi lại API
    if (this.isAlreadyInBusiness(bId)) {
      return of(true);
    }

    // 3. Gọi API admin lấy danh sách page của doanh nghiệp
    return this.adminPageService.list({
      businessId: String(bId),
      limit: 500
    }).pipe(
      map(response => {
        let businessName = `Doanh nghiệp ${bId}`;
        const pages: IPage[] = [];

        // Kiểm tra dữ liệu trả về từ API
        if (response && response.code === 1 && response.data && Array.isArray(response.data.result)) {
          const items = response.data.result;

          // Lấy tên doanh nghiệp từ page đầu tiên nếu có
          if (items.length > 0 && items[0]?.businessName) {
            businessName = String(items[0].businessName).trim() || businessName;
          }

          // Map danh sách trang sang format IPage chuẩn của auth
          for (const item of items) {
            if (!item?.id) continue;
            const channel = item.channel != null ? Number(item.channel) : 1;
            pages.push({
              id: String(item.id),
              name: item.name || String(item.id),
              channel: channel,
              businessId: String(bId),
              avatar: getPageAvatar(channel, item.id),
              isManagePage: true,
              isSyncedData: true
            });
          }
        }

        // 4. Lưu toàn bộ thông tin doanh nghiệp và pages vào auth / local
        this.authService.setAdminImpersonateBusiness(bId, businessName, pages);

        return true;
      }),
      catchError(error => {
        console.warn(`[AdminBusinessContext] Không thể tải danh sách trang cho doanh nghiệp ${bId}:`, error);

        // Fallback an toàn: Dù API lỗi, vẫn tạo context doanh nghiệp để Admin không bị chặn màn hình
        const fallbackName = `Doanh nghiệp ${bId}`;
        this.authService.setAdminImpersonateBusiness(bId, fallbackName, []);

        return of(true);
      })
    );
  }
}
